import { dateTimeErrorMessage, calendarDate, calendarDisplayDate, zonedDate } from '../../../../utils/date-time';
import moment from 'moment';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../../../contexts/AppContext';
import { useParamsContext } from '../../../../contexts/ParamsContext';
import { useAgendaClienteContext } from '../Context';
import { AgendaClienteService } from '../Service';
import { AtendentesService } from '../../../atendentes/Service';
import { AxiosError } from 'axios';
import { ServicosService } from '../../../servicos/Service';
import { AgendamentoPagamentosService } from '../../../agendamentoPagamentos/Service';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useEstabsContext } from '../../../../contexts/EstabsContext';

const horarioService = new AgendaClienteService();
const serviceAtendentes = new AtendentesService();
const servicosService = new ServicosService();
const agendamentoPagamentosService = new AgendamentoPagamentosService();

interface FormHorarioClienteParams {
    date: moment.Moment;
    handlePreviousWeek: () => void;
    handleNextWeek: () => void;
    setDate: React.Dispatch<React.SetStateAction<moment.Moment>>;
    estab: any;
    setEstab: React.Dispatch<React.SetStateAction<any>>;
    servico: any;
    setServico: React.Dispatch<React.SetStateAction<any>>;
    servicosExtras: any[];
    setServicosExtras: React.Dispatch<React.SetStateAction<any[]>>;
    data: any;
    setData: React.Dispatch<React.SetStateAction<any>>;
    atendente: any;
    setAtendente: React.Dispatch<React.SetStateAction<any>>;
    horario: number;
    setHorario: React.Dispatch<React.SetStateAction<number>>;
    showHorarios: boolean;
    setShowHorarios: React.Dispatch<React.SetStateAction<boolean>>;
    submitForm: () => void;
    daysOpen: number;
    setDaysOpen: React.Dispatch<React.SetStateAction<number>>;
    openConfirm: boolean;
    loading: boolean;
    totalPrice: number;
    paymentRequirement: any;
    customerCpfCnpj: string;
    setCustomerCpfCnpj: React.Dispatch<React.SetStateAction<string>>;
    setOpenConfirm: React.Dispatch<React.SetStateAction<boolean>>;
    attendantCount: number | null;
    setAttendantCount: React.Dispatch<React.SetStateAction<number | null>>;
    estabLocked: boolean;
    hasEstabChoice: boolean;
    resetAttendant: () => void;
    canStepBack: boolean;
    stepBack: () => void;
}

export const isValidCustomerDocument = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return digits.length === 11 || digits.length === 14;
};

const FormHorarioClienteContext = createContext<FormHorarioClienteParams | undefined>(undefined);

export const useFormHorarioClienteContext = () => {
    const context = useContext(FormHorarioClienteContext);
    if (!context) {
        throw new Error('useFormHorarioClienteContext must be used within a FormHorarioClienteProvider');
    }
    return context;
};

export const FormHorarioClienteProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { loadAgendamentos } = useAgendaClienteContext();
    const { user } = useAuthContext();
    const { setParams } = useParamsContext();
    const [openConfirm, setOpenConfirm] = useState(false);

    const [loading, setLoading] = useState(false);
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [daysOpen, setDaysOpen] = useState(0);

    const [date, setDate] = useState(moment());

    const handlePreviousWeek = () => {
        setDate((prevDate) => {
            const newDate = moment(prevDate).subtract(7, 'days');
            const today = moment(calendarDisplayDate(new Date(), estab?.timezone)).startOf('day');
            return newDate.isBefore(today) ? today : newDate;
        });
    };

    const handleNextWeek = () => {
        setDate((prevDate) => {
            const newDate = moment(prevDate).add(7, 'days');
            const today = moment(calendarDisplayDate(new Date(), estab?.timezone)).startOf('day');
            const limit = today.clone().add(daysOpen, 'days');
            return newDate.isAfter(limit) ? limit : newDate;
        });
    };

    const [estab, setEstab] = useState({} as any);
    const [servico, setServico] = useState({} as any);
    const [servicosExtras, setServicosExtras] = useState<any[]>([]);
    const [data, setData] = useState({} as any);
    const [atendente, setAtendente] = useState({} as any);
    const [horario, setHorario] = useState(0);
    const [showHorarios, setShowHorarios] = useState(false);
    const [totalPrice, setTotalPrice] = useState(0);
    const [pricesByServiceId, setPricesByServiceId] = useState<Record<string, number>>({});
    useEffect(() => {
        if (estab?._id) setDate(moment(calendarDisplayDate(new Date(), estab.timezone)));
    }, [estab?._id, estab?.timezone]);

    const [paymentRequirement, setPaymentRequirement] = useState<any>(null);
    const [customerCpfCnpj, setCustomerCpfCnpj] = useState('');
    const [attendantCount, setAttendantCount] = useState<number | null>(null);
    const { isClientOnly, clientEstab, estabsUser } = useEstabsContext();
    const estabLocked = isClientOnly && !!clientEstab?._id;
    const hasEstabChoice = !estabLocked && (estabsUser as any[]).filter(({ type }) => type === 'client').length > 1;

    const resetAttendant = () => {
        setAtendente({});
        setServico({});
        setServicosExtras([]);
        setHorario(0);
        setShowHorarios(false);
    };

    // Etapas sem escolha real (um unico profissional ou estabelecimento) sao puladas tambem ao voltar.
    const backTarget = openConfirm
        ? 'confirm'
        : showHorarios
          ? 'horario'
          : atendente?.uid && (attendantCount || 0) > 1
            ? 'servico'
            : estab?._id && hasEstabChoice
              ? 'atendente'
              : null;

    const stepBack = () => {
        if (backTarget === 'confirm') setOpenConfirm(false);
        else if (backTarget === 'horario') setShowHorarios(false);
        else if (backTarget === 'servico') resetAttendant();
        else if (backTarget === 'atendente') {
            resetAttendant();
            setAttendantCount(null);
            setEstab({});
        }
    };

    // Cliente: o estabelecimento vem do seletor do menu (salvo no dispositivo), sem etapa de escolha.
    // Trocar o estabelecimento no menu reinicia as escolhas do agendamento.
    useEffect(() => {
        if (!isClientOnly || !clientEstab?._id) return;
        setEstab(clientEstab);
        setDate(moment(calendarDisplayDate(new Date(), clientEstab.timezone)));
        setAtendente({});
        setServico({});
        setServicosExtras([]);
        setData({});
        setHorario(0);
        setShowHorarios(false);
    }, [isClientOnly, clientEstab]);

    const loadAgendaConfig = async () => {
        if (!atendente?.uid || !estab?._id) return;

        try {
            setLoading(true);

            const { data } = await serviceAtendentes.getAgendaDate(atendente.uid, date.format('YYYY-MM-DD'), estab._id);

            setDaysOpen(data.daysOpen -1 || 0);
        } catch (e) {

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setData({});
        setHorario(0);

        loadAgendaConfig()
    }, [atendente]);

    const extrasIdsKey = useMemo(() => {
        return servicosExtras
            .map((extra: any) => String(extra?._id || ''))
            .filter(Boolean)
            .sort()
            .join('|');
    }, [servicosExtras]);

    useEffect(() => {
        if (!estab?._id || !servico?._id || !data) return;

        const dateValue = data instanceof Date ? data : new Date(data as string);
        if (Number.isNaN(dateValue.getTime())) return;

        const loadPrices = async () => {
            try {
                const servicosIds = [
                    servico?._id,
                    ...servicosExtras.map((extra: any) => extra?._id),
                ].filter(Boolean);

                if (!servicosIds.length) return;

                const response = await servicosService.resolvePrices({
                    estabId: estab._id,
                    date: calendarDate(dateValue),
                    attendantId: atendente?.uid,
                    servicosIds,
                });

                setPricesByServiceId(response?.data || {});
            } catch (error) {
                setPricesByServiceId({});
            }
        };

        loadPrices();
    }, [estab?._id, servico?._id, extrasIdsKey, atendente?.uid, data]);

    useEffect(() => {
        if (!servico?._id) return;

        const servicosIds = [
            servico?._id,
            ...servicosExtras.map((extra: any) => extra?._id),
        ].filter(Boolean);

        if (!servicosIds.length) return;

        const totalFromRules = servicosIds.reduce((total, id) => {
            const price = pricesByServiceId?.[id];
            return total + Number(price || 0);
        }, 0);

        if (Object.keys(pricesByServiceId || {}).length) {
            setTotalPrice(totalFromRules);
            return;
        }

        const extrasPrice = servicosExtras.reduce(
            (total, extra) => total + Number(extra?.price || 0),
            0
        );
        setTotalPrice(Number(servico?.price || 0) + extrasPrice);
    }, [servico, servicosExtras, pricesByServiceId]);

    useEffect(() => {
        if (!estab?._id || !data || !horario) {
            setPaymentRequirement(null);
            return;
        }

        const localDate = calendarDate(data);
        const loadRequirement = async () => {
            try {
                const { data } = await agendamentoPagamentosService.getRequirement({
                    estabId: estab._id,
                    localDate,
                    startMinutes: horario,
                    clientId: user?.uid,
                });
                setPaymentRequirement(data);
            } catch (error) {
                setPaymentRequirement(null);
            }
        };

        loadRequirement();
    }, [estab?._id, data, horario, user?.uid]);

    const submitForm = async () => {
        const agendamentoData = {
            startMinutes: horario,
            estabId: estab._id,
            localDate: calendarDate(data),
            servicoId: servico._id,
            servicosExtrasIds: servicosExtras.map((extra: any) => extra._id),
            attendantId: atendente.uid,
            customerCpfCnpj: customerCpfCnpj.replace(/\D/g, ''),
        };

        try {
            setLoading(true);
            const { data } = await horarioService.createAgendamento(agendamentoData);
            successSnackbar('Agendamento criado com sucesso!');
            loadAgendamentos();

            setParams({ action: 'new', id: data?._id });
        } catch (error) {
            const message = error instanceof AxiosError ? error?.response?.data?.message : undefined;
            const timeError = dateTimeErrorMessage(message);
            if (timeError) { errorSnackbar(timeError); return; }
            if (message === 'conflictHours') {
                errorSnackbar('Este horário acabou de ser ocupado. Escolha outro.');
                // Nova referencia de data faz a lista recarregar sem o horario que foi ocupado.
                setHorario(0);
                setData((current: any) => new Date(current));
            } else if (message === 'attendantServiceMismatch') {
                errorSnackbar('O profissional escolhido não realiza um ou mais serviços selecionados.');
            } else if (String(message || '').includes('CPF/CNPJ')) {
                errorSnackbar('Informe um CPF/CNPJ válido para gerar o Pix.');
            } else {
                errorSnackbar('Não foi possível concluir o agendamento. Tente novamente em instantes.');
            }
        } finally {
            setLoading(false);
        }

        setOpenConfirm(false);
    };

    return (
        <FormHorarioClienteContext.Provider
            value={{
                date,
                handlePreviousWeek,
                handleNextWeek,
                setDate,
                estab,
                daysOpen,
                setDaysOpen,
                setEstab,
                servico,
                setServico,
                servicosExtras,
                setServicosExtras,
                data,
                setData,
                atendente,
                setAtendente,
                horario,
                setHorario,
                showHorarios,
                setShowHorarios,
                totalPrice,
                paymentRequirement,
                customerCpfCnpj,
                setCustomerCpfCnpj,
                submitForm,
                openConfirm,
                setOpenConfirm,
                loading,
                attendantCount,
                setAttendantCount,
                estabLocked,
                hasEstabChoice,
                resetAttendant,
                canStepBack: !!backTarget,
                stepBack,
            }}
        >
            {children}
        </FormHorarioClienteContext.Provider>
    );
};
